import { useState, useEffect, useRef } from 'react'
import { Button, Spin } from 'antd'
import Markdown from 'react-markdown'
import { getReportSkillResult, runReportSkill } from '@/api/stock'

const SKILL_REGISTRY: Record<string, { displayName: string; applicableTypes: string[] }> = {
    'summarize-company': {
        displayName: '年报总结',
        applicableTypes: ['annual', 'interim_h1', 'interim_h2'],
    },
}

type SkillStatus = 'checking' | 'ready' | 'running' | 'done' | 'error'

interface Props {
    reportId: number
    reportType: string
}

export default function SkillPanel({ reportId, reportType }: Props) {
    const [status, setStatus] = useState<SkillStatus>('checking')
    const [result, setResult] = useState('')
    const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null)

    const entry = Object.entries(SKILL_REGISTRY).find(([, meta]) =>
        meta.applicableTypes.includes(reportType),
    )
    const skillName = entry?.[0] ?? null
    const skillMeta = entry?.[1] ?? null

    useEffect(() => {
        readerRef.current?.cancel()
        readerRef.current = null
        if (!skillName) return
        setStatus('checking')
        setResult('')
        getReportSkillResult(reportId, skillName)
            .then(cached => {
                if (cached) {
                    setResult(cached.result_md)
                    setStatus('done')
                } else {
                    setStatus('ready')
                }
            })
            .catch(() => setStatus('ready'))
        return () => {
            readerRef.current?.cancel()
        }
    }, [reportId, skillName])

    const handleRun = async () => {
        if (!skillName) return
        readerRef.current?.cancel()
        setStatus('running')
        setResult('')
        try {
            const reader = await runReportSkill(reportId, skillName)
            readerRef.current = reader
            const decoder = new TextDecoder()
            let buffer = ''
            while (true) {
                const { done, value } = await reader.read()
                buffer += decoder.decode(value ?? new Uint8Array(), { stream: !done })
                let boundary = buffer.indexOf('\n\n')
                while (boundary >= 0) {
                    const frame = buffer.slice(0, boundary)
                    buffer = buffer.slice(boundary + 2)
                    boundary = buffer.indexOf('\n\n')
                    if (!frame || frame.startsWith(':')) continue
                    const event = frame.match(/^event:\s*(.+)$/m)?.[1] ?? 'message'
                    const rawData = frame.match(/^data:\s*(.+)$/m)?.[1]
                    if (!rawData) continue
                    const data = JSON.parse(rawData)
                    if (event === 'token') {
                        setResult(prev => prev + (data.content as string))
                    } else if (event === 'done') {
                        setStatus('done')
                    } else if (event === 'error') {
                        throw new Error(data.detail ?? 'skill failed')
                    }
                }
                if (done) break
            }
            setStatus(prev => (prev === 'running' ? 'done' : prev))
        } catch {
            setStatus('error')
        }
    }

    if (!skillName || !skillMeta) return null

    return (
        <div className="skill-panel">
            <div className="skill-panel__header">
                <span className="skill-panel__title">{skillMeta.displayName}</span>
                <div className="skill-panel__actions">
                    {status === 'checking' && <Spin size="small" />}
                    {status === 'ready' && (
                        <Button size="small" type="primary" onClick={handleRun}>运行</Button>
                    )}
                    {status === 'running' && <Spin size="small" />}
                    {(status === 'done' || status === 'error') && (
                        <Button size="small" onClick={handleRun}>重新运行</Button>
                    )}
                </div>
            </div>
            {status === 'error' && (
                <div className="skill-panel__error">分析失败，请重试</div>
            )}
            {(status === 'running' || status === 'done') && result && (
                <div className="skill-panel__result">
                    <Markdown>{result}</Markdown>
                </div>
            )}
        </div>
    )
}
